import { RefObject, useLayoutEffect } from "react";

const clamp = (value: number, min = 0, max = 1): number =>
    Math.min(max, Math.max(min, value));

const mix = (from: number, to: number, progress: number): number =>
    from + (to - from) * clamp(progress);

const segment = (progress: number, start: number, end: number): number =>
    clamp((progress - start) / (end - start));

const toHexChannel = (value: number): string =>
    Math.round(value).toString(16).padStart(2, "0");

const getStoryBackgroundColor = (progress: number): string => {
  const start = [24, 60, 84] as const; // #183c54
  const end = [18, 44, 69] as const; // #122c45

  return `#${start
      .map((channel, index) =>
          toHexChannel(mix(channel, end[index], progress)),
      )
      .join("")}`;
};

const copyWindow = (
    progress: number,
    enterStart: number,
    enterEnd: number,
    exitStart?: number,
    exitEnd?: number,
): number => {
  const enter = segment(progress, enterStart, enterEnd);
  if (exitStart === undefined || exitEnd === undefined) return enter;
  return Math.min(enter, 1 - segment(progress, exitStart, exitEnd));
};

const easeInOutCubic = (progress: number): number =>
    progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

const easeInOutSine = (progress: number): number =>
    -(Math.cos(Math.PI * progress) - 1) / 2;

const STORY_STEPS = [0, 0.28, 0.5, 0.7, 1] as const;
const INTRO_STEP_DURATION = 1400;
const STEP_DURATION = 980;
const INPUT_RELEASE_DELAY = 70;
const WHEEL_THRESHOLD = 18;
const TOUCH_THRESHOLD = 34;

type ScrollStoryRefs = {
  sectionRef: RefObject<HTMLElement | null>;
  stageRef: RefObject<HTMLDivElement | null>;
  portalRef: RefObject<HTMLElement | null>;
  headerRef: RefObject<HTMLElement | null>;
};

type StoryMetrics = {
  top: number;
  distance: number;
};

/**
 * Wheel, trackpad and touch input can only request the adjacent story state.
 * Every transition has the same duration and ignores inertial input until the
 * selected state is fully reached.
 */
export const useScrollStory = ({
                                 sectionRef,
                                 stageRef,
                                 portalRef,
                                 headerRef,
                               }: ScrollStoryRefs): void => {
  useLayoutEffect(() => {
    const story = sectionRef.current;
    const stage = stageRef.current;
    const portal = portalRef.current;
    const header = headerRef.current;

    if (!story || !stage || !portal || !header) return;

    const copies = Array.from(
        story.querySelectorAll<HTMLElement>("[data-copy]"),
    );
    const photos = Array.from(
        story.querySelectorAll<HTMLElement>("[data-photo]"),
    );
    const marks = Array.from(
        story.querySelectorAll<HTMLElement>("[data-chapter-mark]"),
    );
    const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
    );

    let renderFrame = 0;
    let scrollFrame = 0;
    let releaseTimer = 0;
    let interactionStarted = window.scrollY > 0;
    let viewportWidth = window.innerWidth;
    let inputLocked = false;
    let lockedDirection: -1 | 1 | null = null;
    let wheelAccumulator = 0;
    let touchStartY: number | null = null;
    let currentStep = 0;

    const html = document.documentElement;
    const body = document.body;
    let previousHtmlScrollBehavior = "";
    let previousBodyScrollBehavior = "";
    let scrollBehaviorOverridden = false;
    const previousHtmlOverscroll = html.style.overscrollBehaviorY;
    const previousBodyOverscroll = body.style.overscrollBehaviorY;
    const previousHtmlBackground = html.style.backgroundColor;
    const previousBodyBackground = body.style.backgroundColor;
    const existingThemeColorMetas = Array.from(
        document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'),
    );
    const createdThemeColorMeta = existingThemeColorMetas.length === 0;
    const themeColorMetas = [...existingThemeColorMetas];

    if (createdThemeColorMeta) {
      const meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
      themeColorMetas.push(meta);
    }

    const previousThemeColors = themeColorMetas.map((meta) =>
        meta.getAttribute("content"),
    );
    let lastBrowserColor = "";

    // Prevent the browser's rubber-band area from exposing its default white
    // canvas while this full-screen story is mounted.
    html.style.overscrollBehaviorY = "none";
    body.style.overscrollBehaviorY = "none";

    const setBrowserBackground = (progress: number): void => {
      const color = getStoryBackgroundColor(progress);
      if (color === lastBrowserColor) return;

      lastBrowserColor = color;
      html.style.backgroundColor = color;
      body.style.backgroundColor = color;
      themeColorMetas.forEach((meta) => {
        meta.setAttribute("content", color);
      });
    };

    const syncStageHeight = (): void => {
      const mobilePortrait =
          window.innerWidth <= 900 && window.innerHeight >= window.innerWidth;

      if (mobilePortrait && window.visualViewport) {
        stage.style.setProperty(
            "--stage-height",
            `${Math.round(window.visualViewport.height)}px`,
        );
      } else {
        stage.style.removeProperty("--stage-height");
      }
    };

    const getMetrics = (): StoryMetrics => {
      const rect = story.getBoundingClientRect();
      const stageHeight = stage.getBoundingClientRect().height;

      return {
        top: window.scrollY + rect.top,
        distance: Math.max(0, story.offsetHeight - stageHeight),
      };
    };

    const getProgress = (): number => {
      const metrics = getMetrics();
      return metrics.distance > 0
          ? clamp((window.scrollY - metrics.top) / metrics.distance)
          : 0;
    };

    const getNearestStep = (progress: number): number => {
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      STORY_STEPS.forEach((stepProgress, index) => {
        const distance = Math.abs(progress - stepProgress);
        if (distance < nearestDistance) {
          nearest = index;
          nearestDistance = distance;
        }
      });

      return nearest;
    };

    const setCopy = (progress: number): void => {
      const opacities = [
        1 - segment(progress, 0.15, 0.18),
        copyWindow(progress, 0.2, 0.23, 0.38, 0.41),
        copyWindow(progress, 0.43, 0.46, 0.59, 0.62),
        copyWindow(progress, 0.64, 0.67, 0.8, 0.83),
        copyWindow(progress, 0.85, 0.89),
      ];

      copies.forEach((copy, index) => {
        const opacity = opacities[index] ?? 0;
        const hidden = opacity <= 0.001;

        copy.style.setProperty("--copy-o", opacity.toFixed(3));
        copy.style.setProperty(
            "--copy-y",
            `${mix(20, 0, opacity).toFixed(1)}px`,
        );
        copy.style.visibility = hidden ? "hidden" : "visible";
        copy.setAttribute("aria-hidden", String(hidden));
        copy.classList.toggle("is-active", opacity > 0.55);
      });
    };

    const setPhotos = (progress: number): void => {
      const first = 1 - segment(progress, 0.38, 0.47);
      const second = Math.min(
          segment(progress, 0.36, 0.47),
          1 - segment(progress, 0.62, 0.72),
      );
      const third = segment(progress, 0.62, 0.72);

      [first, second, third].forEach((opacity, index) => {
        const photo = photos[index];
        if (!photo) return;

        photo.style.setProperty("--photo-o", opacity.toFixed(3));
        photo.style.setProperty(
            "--photo-scale",
            mix(1.075, 1.025, opacity).toFixed(3),
        );
      });
    };

    const setPortal = (progress: number): void => {
      const mobile = window.innerWidth <= 900;
      const landscape =
          mobile &&
          window.innerWidth > window.innerHeight &&
          window.innerHeight <= 560;
      const open = segment(progress, 0, 0.18);

      let width: number;
      let height: number;
      let x: number;
      let radius: number;

      if (landscape) {
        width = mix(2, 30, open);
        height = mix(52, 72, open);
        x = mix(-50, 60, open);
        radius = mix(999, 24, open);
      } else if (mobile) {
        const compact = window.innerHeight <= 700;
        width = mix(2, 86, open);
        height = mix(compact ? 54 : 58, compact ? 68 : 72, open);
        x = -50;
        radius = mix(999, 24, open);
      } else {
        width = mix(2, 27, open);
        height = mix(58, 72, open);
        x = mix(-50, 27, open);
        radius = mix(999, 32, open);
      }
      portal.style.setProperty("--portal-w", `${(width+2).toFixed(2)}vw`);
      portal.style.setProperty("--portal-h", `${height.toFixed(2)}vh`);
      portal.style.setProperty("--portal-x", `${x.toFixed(2)}%`);
      portal.style.setProperty("--portal-r", `${radius.toFixed(1)}px`);
      portal.style.maxHeight =
          mobile && !landscape
              ? "calc(var(--stage-height, 100dvh) - 168px - env(safe-area-inset-top))"
              : "";
      portal.style.setProperty(
          "--portal-shadow",
          mix(0, 0.48, open).toFixed(3),
      );
      portal.style.setProperty(
          "--portal-border",
          mix(0.35, 0.14, open).toFixed(3),
      );
      portal.style.top = landscape ? "55%" : "50%";
      stage.style.setProperty(
          "--glow-x",
          `${mix(50, 76, open).toFixed(1)}%`,
      );
    };

    const setProgress = (progress: number): void => {
      stage.style.setProperty("--progress", `${(progress * 100).toFixed(2)}%`);
      const active = getNearestStep(progress);

      marks.forEach((mark, index) => {
        mark.classList.toggle("is-active", index === active);
      });
      header.classList.toggle("is-open", progress > 0.01);
    };

    const renderProgress = (progress: number): void => {
      setBrowserBackground(progress);
      setCopy(progress);
      setPhotos(progress);
      setPortal(progress);
      setProgress(progress);
    };

    const render = (): void => {
      renderFrame = 0;

      const rawProgress = getProgress();
      const visualProgress = scrollFrame
          ? rawProgress
          : STORY_STEPS[getNearestStep(rawProgress)];

      renderProgress(visualProgress);
    };

    const requestRender = (): void => {
      if (!renderFrame) renderFrame = window.requestAnimationFrame(render);
    };

    const overrideScrollBehavior = (): void => {
      if (scrollBehaviorOverridden) return;
      previousHtmlScrollBehavior = html.style.scrollBehavior;
      previousBodyScrollBehavior = body.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      body.style.scrollBehavior = "auto";
      scrollBehaviorOverridden = true;
    };

    const restoreScrollBehavior = (): void => {
      if (!scrollBehaviorOverridden) return;
      html.style.scrollBehavior = previousHtmlScrollBehavior;
      body.style.scrollBehavior = previousBodyScrollBehavior;
      scrollBehaviorOverridden = false;
    };

    const scheduleUnlock = (): void => {
      window.clearTimeout(releaseTimer);

      releaseTimer = window.setTimeout(() => {
        inputLocked = false;
        lockedDirection = null;
        wheelAccumulator = 0;
      }, INPUT_RELEASE_DELAY);
    };

    const animateToStep = (nextStep: number): void => {
      const metrics = getMetrics();
      const startProgress = getProgress();
      const targetProgress = STORY_STEPS[nextStep];
      const targetY = metrics.top + metrics.distance * targetProgress;
      const startY = window.scrollY;
      const distanceY = targetY - startY;
      const isIntroTransition = currentStep === 0 || nextStep === 0;
      const duration = isIntroTransition
          ? INTRO_STEP_DURATION
          : STEP_DURATION;
      const transitionDirection: -1 | 1 =
          targetProgress >= startProgress ? 1 : -1;

      currentStep = nextStep;
      inputLocked = true;
      lockedDirection = transitionDirection;
      wheelAccumulator = 0;

      if (reducedMotion.matches || Math.abs(distanceY) < 1) {
        overrideScrollBehavior();
        window.scrollTo(0, targetY);
        restoreScrollBehavior();
        renderProgress(targetProgress);
        scheduleUnlock();
        return;
      }

      const startedAt = performance.now();
      overrideScrollBehavior();

      const tick = (now: number): void => {
        const elapsed = clamp((now - startedAt) / duration);
        const eased = isIntroTransition
            ? easeInOutSine(elapsed)
            : easeInOutCubic(elapsed);

        window.scrollTo(0, startY + distanceY * eased);
        renderProgress(mix(startProgress, targetProgress, eased));

        if (elapsed < 1) {
          scrollFrame = window.requestAnimationFrame(tick);
          return;
        }

        scrollFrame = 0;
        window.scrollTo(0, targetY);
        renderProgress(targetProgress);
        restoreScrollBehavior();
        scheduleUnlock();
      };

      scrollFrame = window.requestAnimationFrame(tick);
    };

    const isWithinStory = (): boolean => {
      const metrics = getMetrics();
      const y = window.scrollY;

      return y >= metrics.top - 2 && y <= metrics.top + metrics.distance + 2;
    };

    const canControlStory = (direction: -1 | 1): boolean => {
      if (!isWithinStory()) return false;

      const progress = getProgress();
      if (direction < 0 && progress <= 0.001) return false;
      if (direction > 0 && progress >= 0.999) return false;
      return true;
    };

    const shouldBlockTopOverscroll = (direction: -1 | 1): boolean => {
      if (direction > 0 || window.scrollY > 1) return false;

      const metrics = getMetrics();
      return metrics.top <= 1;
    };

    const requestAdjacentStep = (direction: -1 | 1): void => {
      currentStep = getNearestStep(getProgress());
      const nextStep = clamp(
          currentStep + direction,
          0,
          STORY_STEPS.length - 1,
      );

      if (nextStep !== currentStep) animateToStep(nextStep);
    };

    const normalizeWheelDelta = (event: WheelEvent): number => {
      if (event.deltaMode === 1) return event.deltaY * 16;
      if (event.deltaMode === 2) return event.deltaY * window.innerHeight;
      return event.deltaY;
    };

    const handleWheel = (event: WheelEvent): void => {
      const delta = normalizeWheelDelta(event);
      if (delta === 0) return;

      const direction: -1 | 1 = delta > 0 ? 1 : -1;

      if (shouldBlockTopOverscroll(direction)) {
        event.preventDefault();
        wheelAccumulator = 0;
        return;
      }

      // At the final (or initial) boundary, consume only the inertial tail of
      // the gesture that just completed the transition. After the short fixed
      // unlock delay, a new gesture is free to leave the story normally.
      if (
          inputLocked &&
          lockedDirection === direction &&
          isWithinStory()
      ) {
        event.preventDefault();
        return;
      }

      if (!canControlStory(direction)) return;

      event.preventDefault();
      interactionStarted = true;

      if (inputLocked) {
        const reversingAfterTransition =
            !scrollFrame &&
            lockedDirection !== null &&
            direction !== lockedDirection;

        if (reversingAfterTransition) {
          window.clearTimeout(releaseTimer);
          inputLocked = false;
          lockedDirection = null;
          wheelAccumulator = 0;
        } else {
          return;
        }
      }

      wheelAccumulator += delta;
      if (Math.abs(wheelAccumulator) < WHEEL_THRESHOLD) return;
      requestAdjacentStep(wheelAccumulator > 0 ? 1 : -1);
    };

    const handleTouchStart = (event: TouchEvent): void => {
      if (event.touches.length === 1) {
        touchStartY = event.touches[0].clientY;
      }
    };

    const handleTouchMove = (event: TouchEvent): void => {
      if (touchStartY === null || event.touches.length !== 1) return;

      const currentY = event.touches[0].clientY;
      const delta = touchStartY - currentY;
      if (Math.abs(delta) < 1) return;

      const direction: -1 | 1 = delta > 0 ? 1 : -1;

      if (shouldBlockTopOverscroll(direction)) {
        event.preventDefault();
        return;
      }

      if (
          inputLocked &&
          lockedDirection === direction &&
          isWithinStory()
      ) {
        event.preventDefault();
        return;
      }

      if (!canControlStory(direction)) return;

      event.preventDefault();
      interactionStarted = true;

      if (inputLocked) {
        const reversingAfterTransition =
            !scrollFrame &&
            lockedDirection !== null &&
            direction !== lockedDirection;

        if (reversingAfterTransition) {
          window.clearTimeout(releaseTimer);
          inputLocked = false;
          lockedDirection = null;
        } else {
          return;
        }
      }

      if (Math.abs(delta) < TOUCH_THRESHOLD) return;

      touchStartY = currentY;
      requestAdjacentStep(direction);
    };

    const handleTouchEnd = (): void => {
      touchStartY = null;
    };

    const handleScroll = (): void => {
      interactionStarted = true;
      requestRender();
    };

    const handleResize = (): void => {
      const widthChanged = Math.abs(window.innerWidth - viewportWidth) > 1;

      // Mobile browsers change only viewport height while hiding their address
      // bar. Keeping the original stage height prevents the content from
      // dropping before the first animation. A real orientation/layout change
      // also changes width, so only that case is allowed to resync the stage.
      if (widthChanged) {
        viewportWidth = window.innerWidth;
        syncStageHeight();
      }

      if (interactionStarted || widthChanged) requestRender();
    };

    const handleVisualViewportResize = (): void => {
      if (interactionStarted) requestRender();
    };

    syncStageHeight();
    currentStep = getNearestStep(getProgress());
    renderProgress(STORY_STEPS[currentStep]);

    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener(
        "resize",
        handleVisualViewportResize,
    );
    reducedMotion.addEventListener?.("change", requestRender);

    return () => {
      if (renderFrame) window.cancelAnimationFrame(renderFrame);
      if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
      window.clearTimeout(releaseTimer);
      restoreScrollBehavior();
      html.style.overscrollBehaviorY = previousHtmlOverscroll;
      body.style.overscrollBehaviorY = previousBodyOverscroll;
      html.style.backgroundColor = previousHtmlBackground;
      body.style.backgroundColor = previousBodyBackground;

      themeColorMetas.forEach((meta, index) => {
        if (createdThemeColorMeta) {
          meta.remove();
          return;
        }

        const previousColor = previousThemeColors[index];
        if (previousColor === null) meta.removeAttribute("content");
        else meta.setAttribute("content", previousColor);
      });

      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener(
          "resize",
          handleVisualViewportResize,
      );
      reducedMotion.removeEventListener?.("change", requestRender);
    };
  }, [headerRef, portalRef, sectionRef, stageRef]);
};
